<template>
  <div class="vn-root">
    <header-nav auto-hide></header-nav>
    <!-- ================= 画面切换过渡（结局淡出 → 标题淡入 等） ================= -->
    <transition name="screen-fade" mode="out-in">
    <!-- ================= 标题画面 ================= -->
    <div v-if="screen === 'title'" class="vn-title" key="title">
      <video class="title-bg" :src="titleBgVideo" autoplay muted loop playsinline></video>
      <div class="star-layer">
        <span v-for="(s, i) in stars" :key="i" class="star" :style="starStyle(s)"></span>
      </div>
      <div class="title-vignette"></div>
      <div class="title-content">
        <div class="title-sub">粉裙星物语 · 视觉小说</div>
        <h1 class="title-main">粉星忘语</h1>
        <h2 class="title-sub2">只为你安眠</h2>
        <div class="title-btns">
          <div class="continue-row">
            <button class="vn-btn" :class="{ disabled: !hasSave }" @click="loadGame">继续游戏</button>
            <span class="progress-tag">{{ saveProgress }}</span>
          </div>
          <button class="vn-btn main wide" @click="startNew">开始新游戏</button>
          <button class="vn-btn" @click="openTitlePanel('chapter')">章节选择</button>
          <button class="vn-btn" @click="openTitlePanel('load')">读取存档</button>
          <button class="vn-btn" @click="openTitlePanel('setting')">设置</button>
          <button class="vn-btn" @click="openTitlePanel('gallery')">插画鉴赏</button>
          <button class="vn-btn" @click="openTitlePanel('script')">剧情文本</button>
          <button class="vn-btn" @click="openTitlePanel('about')">关于粉星忘语</button>
        </div>
        <div class="title-tip">点击画面推进剧情 · 可存档 / 读档 / 快进 / 自动</div>
      </div>

      <!-- 章节选择 -->
      <div v-if="titlePanel === 'chapter'" class="vn-modal" @click.self="closeTitlePanel">
        <div class="vn-modal-card">
          <button class="vn-modal-close" @click="closeTitlePanel">×</button>
          <h3>章节选择</h3>
          <div class="chapter-list">
            <button v-for="(ch, i) in chapters" :key="i" class="vn-btn chapter-item" @click="gotoChapter(i)">
              <span class="ch-title">{{ ch.title }}</span>
              <span class="ch-sub">{{ ch.subtitle }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- 读取存档 -->
      <div v-if="titlePanel === 'load'" class="vn-modal" @click.self="closeTitlePanel">
        <div class="vn-modal-card save-card">
          <button class="vn-modal-close" @click="closeTitlePanel">×</button>
          <h3>读取进度</h3>
          <div class="save-grid">
            <div v-for="slot in 6" :key="slot" class="save-slot" @click="slotAction(slot - 1)">
              <div class="slot-title">存档 {{ slot }}</div>
              <div class="slot-info">{{ saveSlots[slot - 1] ? saveSlots[slot - 1].chapter + ' · ' + saveSlots[slot - 1].time : '空' }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 设置 -->
      <div v-if="titlePanel === 'setting'" class="vn-modal" @click.self="closeTitlePanel">
        <div class="vn-modal-card setting-card">
          <button class="vn-modal-close" @click="closeTitlePanel">×</button>
          <h3>设置</h3>
          <div class="setting-row"><span>BGM 音量</span><input type="range" min="0" max="100" v-model.number="bgmVol" @input="applyVolumes"><span class="val">{{ bgmVol }}</span></div>
          <div class="setting-row"><span>音效音量</span><input type="range" min="0" max="100" v-model.number="seVol" @input="applyVolumes"><span class="val">{{ seVol }}</span></div>
          <div class="setting-row"><span>文字速度</span><input type="range" min="10" max="100" v-model.number="textSpeed"><span class="val">{{ textSpeed }}</span></div>
          <div class="setting-row"><span>自动间隔</span><input type="range" min="1" max="8" v-model.number="autoDelay"><span class="val">{{ autoDelay }}s</span></div>
          <div class="setting-row"><span>音乐静音</span><button class="vn-btn small setting-mute" :class="{ muted: bgmMuted }" @click="toggleMute">{{ bgmMuted ? '已静音 · 点击恢复' : '未静音 · 点击静音' }}</button></div>
        </div>
      </div>

      <!-- 插画鉴赏 -->
      <div v-if="titlePanel === 'gallery'" class="vn-modal" @click.self="closeTitlePanel">
        <div class="vn-modal-card gallery-card">
          <button class="vn-modal-close" @click="closeTitlePanel">×</button>
          <h3>插画鉴赏</h3>
          <div class="gallery-grid">
            <div v-for="(cg, i) in cgList" :key="i" class="gallery-item" @click="galleryView = cg.img">
              <img :src="img(cg.img)" :alt="cg.label">
              <span class="gallery-label">{{ cg.label }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-if="galleryView" class="vn-modal gallery-view" @click.self="galleryView = ''">
        <img :src="img(galleryView)" alt="">
        <button class="vn-modal-close" @click="galleryView = ''">×</button>
      </div>

      <!-- 剧情文本（完整剧本浏览弹窗）：打开淡入 / 关闭淡出 -->
      <transition name="vn-modal-fade">
        <div v-if="titlePanel === 'script'" class="vn-modal script-modal" @click.self="closeTitlePanel">
          <div class="vn-modal-card script-card">
          <video v-if="scriptBgVideo" ref="scriptBgVideo" class="script-bg" :src="scriptBgVideo" autoplay muted loop playsinline></video>
          <div class="script-tools" :class="{ 'script-tools-under-nav': navShown }">
            <button class="script-pause-btn" @click="toggleScriptAuto" :title="(scriptAuto || scriptAutoPending) ? '暂停自动下滑' : '继续自动下滑'">
              {{ (scriptAuto || scriptAutoPending) ? '❚❚ 暂停' : '▶ 继续' }}
            </button>
            <button class="vn-modal-close" @click="closeTitlePanel">×</button>
          </div>
          <div class="script-body">
            <h3>粉星忘语：只为你安眠-剧情文本</h3>
            <div class="script-tip">{{ scriptTipText }}</div>
            <div class="script-list" ref="scriptListRef" @wheel="onScriptUserScroll" @mousedown="onScriptUserScroll" @touchstart="onScriptUserScroll" @scroll.passive="onScriptScroll">
              <script-block v-for="(b, i) in visibleScript" :key="i" :b="b" />
            </div>
          </div>
        </div>
      </div>
      </transition>

      <!-- 关于粉星忘语：打开淡入 / 关闭淡出 -->
      <transition name="vn-modal-fade">
        <div v-if="titlePanel === 'about'" class="vn-modal" @click.self="closeTitlePanel">
          <div class="vn-modal-card about-card">
            <button class="vn-modal-close" @click="closeTitlePanel">×</button>
          <h3>关于粉星忘语</h3>
          <div class="about-text">
            <p>《粉星忘语 · 只为你安眠》是一段关于"意识"与"人间温度"的视觉小说。</p>
            <p>时空乱流落幕后的新千年，科学家布洛在禁域的焦黑废墟中，捡回一枚不属于任何已知文明的量子芯片。十年里，他始终无法为原型机"若依"复刻出代码之外那一点"恰到好处的笨"。而这一次，芯片中封存的星云，或许就是答案。</p>
            <p>时空乱流之后的世界里，一个 "从天而降的星星，来人间被爱" 感受人间温暖</p>
            <p>机器少女若依在实验室里苏醒，被博士和小满当成家人，教她说话、做饭、出门、交朋友。她第一次吃糖葫芦、第一次在地铁上让座、第一次穿喜欢的裙子、第一次坐飞机看云海…… 每一个 "第一次" 都笨拙又闪闪发光。</p>
            <p>但这个世界藏着秘密：她不能被人发现；她不会长大，也不会老；而天空的裂缝，正在一点点重新裂开。她以为自己只是一个小女孩，其实她是从天上掉下来的一颗星星，来体验她曾守护过的人间。若依不会衰老而身边的人会老去、会牺牲，她学会照顾自己、替大家记住人间，独自守城百年，然后替所有睡着的人，把春天种完。全剧结局唯一，"感受不同，命运相同"。</p>
            <p>剧情 · 粉裙星物语</p>
          </div>
        </div>
      </div>
      </transition>
    </div>

    <!-- ================= 游戏画面 ================= -->
    <div v-else-if="screen === 'game'" class="vn-game" :class="{ 'game-faded': gameFaded }" key="game">
      <transition name="fade-bg">
        <video v-if="curBgVideoUrl" class="layer-bg" :src="curBgVideoUrl" autoplay muted loop playsinline :key="'bgv' + curBg"></video>
        <div v-else-if="curBg" class="layer-bg" :style="{ backgroundImage: 'url(' + (img(curBg) || img(curBg + '.jpg')) + ')' }" :key="curBg"></div>
      </transition>

      <transition name="fade-bg">
        <div v-if="curCg" class="layer-cg" :style="{ backgroundImage: 'url(' + (img(curCg) || img(curCg + '.jpg')) + ')' }" :key="curCg"></div>
      </transition>

      <!-- 三位置立绘层 -->
      <div class="layer-stand">
        <transition name="fade-stand">
          <div v-if="stands.left.show" class="stand-item stand-left" :key="stands.left.img" @click.stop>
            <img :src="img(stands.left.img)" :alt="stands.left.img">
          </div>
        </transition>
        <transition name="fade-stand">
          <div v-if="stands.center.show" class="stand-item stand-center" :key="stands.center.img" @click.stop>
            <img :src="img(stands.center.img)" :alt="stands.center.img">
          </div>
        </transition>
        <transition name="fade-stand">
          <div v-if="stands.right.show" class="stand-item stand-right" :key="stands.right.img" @click.stop>
            <img :src="img(stands.right.img)" :alt="stands.right.img">
          </div>
        </transition>
      </div>

      <div :class="['fx-layer', { shake: fxShake }]">
        <transition name="fade-bg">
          <div v-if="fxFlash" class="fx-flash" :style="{ backgroundColor: fxFlash }"></div>
        </transition>
      </div>

      <transition name="fade-chapter">
        <div v-if="chapterShow" class="chapter-layer">
          <div class="chapter-card">
            <div class="chapter-label">— CHAPTER —</div>
            <h2>{{ chapterTitle }}</h2>
            <div class="chapter-sub">{{ chapterSubtitle }}</div>
            <div class="chapter-tip">点击继续</div>
          </div>
        </div>
      </transition>

      <transition name="fade-chapter">
        <div v-if="optionsShow" class="option-layer" @click.stop>
          <div class="option-box">
            <div class="option-hint">作出你的选择</div>
            <button v-for="(c, i) in curOptions" :key="i" class="vn-btn option" @click="chooseOption(i)">
              {{ c.label }}
            </button>
          </div>
        </div>
      </transition>

      <div v-if="dialogShow" class="dialog-wrap" @click="onClick">
        <div class="dialog-box">
          <div v-if="speaker" class="speaker-tag" :class="'sp-' + namePos" :style="{ background: speakerColor(speaker) }">{{ speaker }}</div>
          <div class="dialog-text" :style="{ fontSize: (13 + (100 - textSpeed) / 12) + 'px', color: curTextColor || undefined }">
            <span v-for="(c, i) in shownChars" :key="i" :style="c.color ? { color: c.color } : null">{{ c.ch }}</span>
            <span v-if="typing" class="cursor">▌</span>
          </div>
          <div v-if="!typing" class="next-arrow">▼</div>
        </div>
      </div>

      <div class="sys-bar" :class="{ 'sys-under-nav': navShown }" @click.stop>
        <button class="sys-btn" :class="{ active: autoMode }" @click="toggleAuto">自动</button>
        <button class="sys-btn" :class="{ active: skipMode }" @click="toggleSkip">快进</button>
        <button class="sys-btn" @click="goBackLine">退回</button>
        <button class="sys-btn" @click="openSave">存档</button>
        <button class="sys-btn" @click="openLoad">读档</button>
        <button class="sys-btn" @click="openSetting">设置</button>
        <button class="sys-btn" @click="backToTitle">标题</button>
      </div>

      <div v-if="savePanel" class="vn-modal" @click.self="savePanel = false">
        <div class="vn-modal-card save-card">
          <button class="vn-modal-close" @click="savePanel = false">×</button>
          <h3>{{ savePanel === 'save' ? '保存进度' : '读取进度' }}</h3>
          <div class="save-grid">
            <div v-for="slot in 6" :key="slot" class="save-slot" @click="slotAction(slot - 1)">
              <div class="slot-title">存档 {{ slot }}</div>
              <div class="slot-info">{{ saveSlots[slot - 1] ? saveSlots[slot - 1].chapter + ' · ' + saveSlots[slot - 1].time : '空' }}</div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showSetting" class="vn-modal" @click.self="closeSetting">
        <div class="vn-modal-card setting-card">
          <button class="vn-modal-close" @click="closeSetting">×</button>
          <h3>设置</h3>
          <div class="setting-row"><span>BGM 音量</span><input type="range" min="0" max="100" v-model.number="bgmVol" @input="applyVolumes"><span class="val">{{ bgmVol }}</span></div>
          <div class="setting-row"><span>音效音量</span><input type="range" min="0" max="100" v-model.number="seVol" @input="applyVolumes"><span class="val">{{ seVol }}</span></div>
          <div class="setting-row"><span>文字速度</span><input type="range" min="10" max="100" v-model.number="textSpeed"><span class="val">{{ textSpeed }}</span></div>
          <div class="setting-row"><span>自动间隔</span><input type="range" min="1" max="8" v-model.number="autoDelay"><span class="val">{{ autoDelay }}s</span></div>
          <div class="setting-row"><span>音乐静音</span><button class="vn-btn small setting-mute" :class="{ muted: bgmMuted }" @click="toggleMute">{{ bgmMuted ? '已静音 · 点击恢复' : '未静音 · 点击静音' }}</button></div>
        </div>
      </div>
    </div>

    <!-- ================= 结局画面 ================= -->
    <div v-else-if="screen === 'ending'" class="vn-title ending-screen" key="ending">
      <div class="title-bg" :style="{ backgroundImage: 'url(' + endingBg + ')' }"></div>
      <div class="title-vignette"></div>
      <div class="title-content">
        <div class="ending-label">{{ endingLabel }}</div>
        <h1 class="title-main">{{ endingTitle }}</h1>
        <div class="ending-text">{{ endingText }}</div>
        <div class="title-btns">
          <button class="vn-btn main" @click="backToTitle">返回标题</button>
          <button class="vn-btn" @click="startNew">重新开始</button>
        </div>
        <div class="ending-auto-hint">片刻后将自动返回标题页…</div>
      </div>
    </div>
    </transition>

    <!-- ================= 场景过渡层（页面淡出 → 过渡媒体 → 新剧情淡入） ================= -->
    <transition name="fade-media">
      <div v-if="transActive" class="trans-layer">
        <video v-if="transIsVideo" class="trans-media" :src="transMedia" autoplay muted loop playsinline></video>
        <img v-else class="trans-media" :src="transMedia" alt="">
      </div>
    </transition>
  </div>
</template>

<script>
/* ============================================================
   剧情与角色数据：全部在 ./story-data.js 中维护
   图片：自动加载 ./story-img 文件夹内所有 jpg/png（文件名即引用名）
   ============================================================ */
import { STORY, CHARACTERS, TEXT_COLORS } from './story-data'
import FULL_SCRIPT from './full-script.json'
import HeaderNav from "@/components/HeaderNav.vue";
import bus from '@/utils/bus'

/* 自动加载 story-img 文件夹内全部图片（新增图片放进文件夹即可用） */
const imgContext = require.context('./story-img', false, /\.(jpg|jpeg|png|gif)$/)
const IMG_MAP = {}
imgContext.keys().forEach(k => {
  IMG_MAP[k.replace('./', '')] = imgContext(k)
})

/* 标题页背景视频 */
const titleBgVideo = require('./videos/标题页背景.mp4')

/* 自动加载 videos 文件夹内全部视频（供场景过渡使用，文件名即引用名） */
const videoContext = require.context('./videos', false, /\.(mp4|webm|ogg)$/)
const VIDEO_MAP = {}
videoContext.keys().forEach(k => {
  VIDEO_MAP[k.replace('./', '')] = videoContext(k)
})

/* ============================================================
   真实音乐文件映射：自动扫描同目录 ./music 文件夹（新增 mp3 放进去即可用）
   剧情数据中 bgm 字段既可写合成音名（lab/desert...），也可写这里的文件名
   ============================================================ */
const musicCtx = require.context('./music', false, /\.(mp3|wav|flac|ogg|m4a|aac)$/i)
const BGM_FILE_MAP = {}
musicCtx.keys().forEach(k => {
  const fileName = k.replace(/^\.\//, '') // 完整文件名（含扩展名）
  const baseName = fileName.replace(/\.[^.]+$/, '') // 不带扩展名的名字
  BGM_FILE_MAP[fileName] = musicCtx(k)
  BGM_FILE_MAP[baseName] = musicCtx(k)
})

/* 自动加载 voice 目录配音台词（新增 wav 放进去即可用） */
const voiceCtx = require.context('./voice', false, /\.(wav|mp3|ogg|m4a)$/i)
const VOICE_MAP = {}
voiceCtx.keys().forEach(k => {
  const fileName = k.replace(/^\.\//, '')
  const baseName = fileName.replace(/\.[^.]+$/, '')
  VOICE_MAP[fileName] = voiceCtx(k)
  VOICE_MAP[baseName] = voiceCtx(k)
})

/* ============================================================
   Web Audio 音效引擎（合成音 + 真实音乐文件，均支持剧情切换）
   ============================================================ */
class AudioEngine {
  constructor () {
    this.ctx = null
    this.master = null
    this.bgmGain = null
    this.seGain = null
    this.bgmNodes = []
    this.currentBgm = null
    this.bgmVol = 0.6
    this.seVol = 0.8
    this._birdsTimer = null
    /* 真实音乐文件播放（HTMLAudio，loop 循环） */
    this.bgmAudio = null
    this.bgmAudioName = null
    this.bgmMuted = false
  }

  init () {
    if (this.ctx) return
    const AC = window.AudioContext || window.webkitAudioContext
    if (!AC) return
    this.ctx = new AC()
    this.master = this.ctx.createGain()
    this.master.gain.value = 0.9
    this.master.connect(this.ctx.destination)
    this.bgmGain = this.ctx.createGain()
    this.bgmGain.gain.value = this.bgmVol
    this.bgmGain.connect(this.master)
    this.seGain = this.ctx.createGain()
    this.seGain.gain.value = this.seVol
    this.seGain.connect(this.master)
  }

  resume () {
    if (this.ctx && this.ctx.state === 'suspended') this.ctx.resume()
  }

  setVolumes (bgm, se) {
    this.bgmVol = bgm
    this.seVol = se
    if (this.bgmGain) this.bgmGain.gain.value = this.bgmMuted ? 0 : bgm
    if (this.seGain) this.seGain.gain.value = se
    if (this.bgmAudio) this.bgmAudio.volume = this.bgmMuted ? 0 : bgm
  }

  /* BGM 静音开关（只静音乐，不影响音效） */
  muteBgm (muted) {
    this.bgmMuted = !!muted
    if (this.bgmGain) this.bgmGain.gain.value = this.bgmMuted ? 0 : this.bgmVol
    if (this.bgmAudio) this.bgmAudio.volume = this.bgmMuted ? 0 : this.bgmVol
  }

  _note (freq, t0, dur, type, gain, dest, filterFreq) {
    const osc = this.ctx.createOscillator()
    osc.type = type || 'sine'
    osc.frequency.setValueAtTime(freq, t0)
    const g = this.ctx.createGain()
    g.gain.setValueAtTime(0.0001, t0)
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.05)
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
    osc.connect(g)
    if (filterFreq) {
      const f = this.ctx.createBiquadFilter()
      f.type = 'lowpass'
      f.frequency.value = filterFreq
      g.connect(f)
      f.connect(dest)
    } else {
      g.connect(dest)
    }
    osc.start(t0)
    osc.stop(t0 + dur + 0.05)
    return osc
  }

  _noise (t0, dur, gain, filterType, freq, dest) {
    const len = Math.max(1, Math.floor(this.ctx.sampleRate * dur))
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate)
    const data = buf.getChannelData(0)
    for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len)
    const src = this.ctx.createBufferSource()
    src.buffer = buf
    const g = this.ctx.createGain()
    g.gain.setValueAtTime(gain, t0)
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
    if (filterType) {
      const f = this.ctx.createBiquadFilter()
      f.type = filterType
      f.frequency.value = freq || 1000
      src.connect(f)
      f.connect(g)
    } else {
      src.connect(g)
    }
    g.connect(dest)
    src.start(t0)
    return src
  }

  _stopBgm () {
    this.bgmNodes.forEach(n => { try { n.stop() } catch (e) { /* 忽略已停止节点 */ } })
    this.bgmNodes = []
    this.currentBgm = null
    if (this.bgmAudio) {
      this.bgmAudio.pause()
      this.bgmAudioName = null
    }
    if (this._birdsTimer) { clearInterval(this._birdsTimer); this._birdsTimer = null }
  }

  /* 播放真实音乐文件（循环播放）：name 为 BGM_FILE_MAP 中的文件名（可带可不带扩展名） */
  playBgmFile (name) {
    const url = BGM_FILE_MAP[name]
    if (!url) return
    if (this.bgmAudioName === name && this.bgmAudio && !this.bgmAudio.paused) return
    this._stopBgm()
    this.currentBgm = name
    this.bgmAudioName = name
    if (!this.bgmAudio) {
      this.bgmAudio = new Audio()
      this.bgmAudio.loop = true
      this.bgmAudio.addEventListener('error', () => {
        this.bgmAudioName = null
      })
    }
    this.bgmAudio.src = url
    this.bgmAudio.volume = this.bgmMuted ? 0 : this.bgmVol
    this.bgmAudio.play().catch(() => { /* 浏览器拦截时静默，等待下次用户点击后自动恢复 */ })
  }

  /* 可用 BGM：title / lab / desert / underground / morning / tension（合成音）
     或 ./music 目录下的音乐文件名（真实文件，循环播放） */
  playBgm (name) {
    if (!this.ctx) return
    this.resume()
    /* 命中真实音乐文件 → 走文件循环播放 */
    if (name && BGM_FILE_MAP[name]) {
      this.playBgmFile(name)
      return
    }
    if (this.currentBgm === name) return
    this._stopBgm()
    if (!name) return
    this.currentBgm = name
    const t = this.ctx.currentTime + 0.1
    const defs = {
      title: () => this._bgmPad([[261.63, 329.63, 392.0], [293.66, 349.23, 440.0], [220, 261.63, 329.63]], t, 0.05, 'sine', 0.06, 1600),
      lab: () => {
        this._bgmPad([[174.61, 220, 261.63], [155.56, 196, 233.08]], t, 0.045, 'triangle', 0.07, 1200)
        this._bgmArp([220, 261.63, 329.63, 392, 329.63, 261.63], t, 0.055, 'triangle', 0.045, 1.2)
      },
      desert: () => {
        this._bgmPad([[110, 146.83, 174.61], [103.83, 138.59, 164.81]], t, 0.08, 'sawtooth', 0.035, 500)
        this._bgmDrone(55, t, 0.09)
      },
      underground: () => {
        this._bgmPad([[220, 277.18, 329.63], [246.94, 293.66, 369.99]], t, 0.04, 'triangle', 0.06, 1400)
        this._bgmPulse(t, 0.045)
      },
      morning: () => {
        this._bgmArp([261.63, 329.63, 392, 523.25, 392, 329.63, 261.63, 392], t, 0.05, 'triangle', 0.06, 1.1)
      },
      tension: () => {
        this._bgmPad([[233.08, 246.94], [220, 233.08]], t, 0.09, 'sawtooth', 0.04, 700)
      }
    }
    if (defs[name]) defs[name]()
    if (name === 'morning') this._birdsAmbient()
  }

  _bgmPad (chords, t, gap, type, vol, filterFreq) {
    chords.forEach((ch, ci) => {
      const start = t + ci * gap * 4
      ch.forEach((f, i) => {
        const osc = this.ctx.createOscillator()
        osc.type = type
        osc.frequency.value = f
        const g = this.ctx.createGain()
        g.gain.setValueAtTime(0.0001, start)
        g.gain.linearRampToValueAtTime(vol, start + 1.5)
        g.gain.linearRampToValueAtTime(vol * 0.8, start + 4)
        g.gain.linearRampToValueAtTime(0.0001, start + 6.5)
        const f2 = this.ctx.createBiquadFilter()
        f2.type = 'lowpass'
        f2.frequency.value = filterFreq
        const lfo = this.ctx.createOscillator()
        lfo.frequency.value = 0.08 + i * 0.03
        const lg = this.ctx.createGain()
        lg.gain.value = filterFreq * 0.25
        lfo.connect(lg)
        lg.connect(f2.frequency)
        osc.connect(f2)
        f2.connect(g)
        g.connect(this.bgmGain)
        osc.start(start)
        osc.stop(start + 6.5)
        lfo.start(start)
        lfo.stop(start + 6.5)
        this.bgmNodes.push(osc, lfo)
      })
    })
  }

  _bgmArp (notes, t, vol, type, step, loopDur) {
    let time = t
    let i = 0
    const total = loopDur * 3
    while (time - t < total) {
      const f = notes[i % notes.length]
      const osc = this.ctx.createOscillator()
      osc.type = type
      osc.frequency.value = f
      const g = this.ctx.createGain()
      g.gain.setValueAtTime(0.0001, time)
      g.gain.exponentialRampToValueAtTime(vol, time + 0.02)
      g.gain.exponentialRampToValueAtTime(0.0001, time + step * 0.9)
      osc.connect(g)
      g.connect(this.bgmGain)
      osc.start(time)
      osc.stop(time + step)
      this.bgmNodes.push(osc)
      time += step
      i++
    }
  }

  _bgmDrone (freq, t, vol) {
    const osc = this.ctx.createOscillator()
    osc.type = 'sawtooth'
    osc.frequency.value = freq
    const f = this.ctx.createBiquadFilter()
    f.type = 'lowpass'
    f.frequency.value = 180
    const g = this.ctx.createGain()
    g.gain.value = vol
    osc.connect(f)
    f.connect(g)
    g.connect(this.bgmGain)
    osc.start(t)
    osc.stop(t + 12)
    this.bgmNodes.push(osc)
  }

  _bgmPulse (t, vol) {
    let time = t
    for (let i = 0; i < 40; i++) {
      const osc = this.ctx.createOscillator()
      osc.type = 'square'
      osc.frequency.value = 880 + Math.sin(i * 0.7) * 220
      const g = this.ctx.createGain()
      g.gain.setValueAtTime(0.0001, time)
      g.gain.exponentialRampToValueAtTime(vol * 0.35, time + 0.01)
      g.gain.exponentialRampToValueAtTime(0.0001, time + 0.1)
      osc.connect(g)
      g.connect(this.bgmGain)
      osc.start(time)
      osc.stop(time + 0.12)
      this.bgmNodes.push(osc)
      time += 0.35
    }
  }

  _birdsAmbient () {
    const chirp = () => {
      const time = this.ctx.currentTime
      const f = 2400 + Math.random() * 1600
      this._note(f, time, 0.12, 'sine', 0.025, this.seGain)
      this._note(f * 1.3, time + 0.11, 0.1, 'sine', 0.02, this.seGain)
    }
    this._birdsTimer = setInterval(chirp, 3800)
    setTimeout(chirp, 300)
  }

  /* 可用音效：click/type/buzz/alarm/boom/scan/machine/boot/door/foot/bell/birds/pour/power/rise */
  playSe (name) {
    if (!this.ctx) return
    this.resume()
    const t = this.ctx.currentTime + 0.01
    const g = this.seGain
    const se = {
      click: () => this._note(1200, t, 0.05, 'square', 0.05, g),
      type: () => this._noise(t, 0.02, 0.02, 'highpass', 4000, g),
      buzz: () => {
        for (let i = 0; i < 6; i++) this._note(880, t + i * 0.08, 0.05, 'square', 0.06, g)
      },
      alarm: () => {
        for (let i = 0; i < 8; i++) this._note(i % 2 === 0 ? 1100 : 880, t + i * 0.16, 0.12, 'square', 0.07, g)
      },
      boom: () => {
        this._noise(t, 1.6, 0.5, 'lowpass', 300, g)
        this._note(70, t, 1.4, 'sine', 0.4, g, 200)
      },
      scan: () => {
        const osc = this.ctx.createOscillator()
        osc.type = 'sine'
        osc.frequency.setValueAtTime(300, t)
        osc.frequency.exponentialRampToValueAtTime(1800, t + 0.4)
        const gg = this.ctx.createGain()
        gg.gain.setValueAtTime(0.05, t)
        gg.gain.exponentialRampToValueAtTime(0.0001, t + 0.45)
        osc.connect(gg)
        gg.connect(g)
        osc.start(t)
        osc.stop(t + 0.5)
      },
      machine: () => {
        this._noise(t, 0.3, 0.12, 'bandpass', 400, g)
        this._note(180, t, 0.08, 'square', 0.04, g)
        this._note(240, t + 0.15, 0.08, 'square', 0.04, g)
      },
      boot: () => {
        const osc = this.ctx.createOscillator()
        osc.type = 'sawtooth'
        osc.frequency.setValueAtTime(80, t)
        osc.frequency.exponentialRampToValueAtTime(600, t + 1.2)
        const f = this.ctx.createBiquadFilter()
        f.type = 'lowpass'
        f.frequency.setValueAtTime(200, t)
        f.frequency.exponentialRampToValueAtTime(3000, t + 1.2)
        const gg = this.ctx.createGain()
        gg.gain.setValueAtTime(0.0001, t)
        gg.gain.linearRampToValueAtTime(0.1, t + 0.3)
        gg.gain.linearRampToValueAtTime(0.0001, t + 1.3)
        osc.connect(f)
        f.connect(gg)
        gg.connect(g)
        osc.start(t)
        osc.stop(t + 1.4)
      },
      door: () => {
        const osc = this.ctx.createOscillator()
        osc.type = 'triangle'
        osc.frequency.setValueAtTime(900, t)
        osc.frequency.exponentialRampToValueAtTime(150, t + 0.5)
        const gg = this.ctx.createGain()
        gg.gain.setValueAtTime(0.06, t)
        gg.gain.exponentialRampToValueAtTime(0.0001, t + 0.55)
        osc.connect(gg)
        gg.connect(g)
        osc.start(t)
        osc.stop(t + 0.6)
      },
      foot: () => this._noise(t, 0.06, 0.06, 'lowpass', 400, g),
      bell: () => {
        this._note(880, t, 0.2, 'sine', 0.06, g)
        this._note(1108.73, t + 0.25, 0.3, 'sine', 0.06, g)
      },
      birds: () => this._note(2600, t, 0.15, 'sine', 0.03, g),
      pour: () => this._noise(t, 0.8, 0.08, 'bandpass', 1500, g),
      power: () => {
        const osc = this.ctx.createOscillator()
        osc.type = 'sine'
        osc.frequency.setValueAtTime(200, t)
        osc.frequency.exponentialRampToValueAtTime(1400, t + 0.9)
        const gg = this.ctx.createGain()
        gg.gain.setValueAtTime(0.0001, t)
        gg.gain.exponentialRampToValueAtTime(0.09, t + 0.3)
        gg.gain.exponentialRampToValueAtTime(0.0001, t + 1)
        osc.connect(gg)
        gg.connect(g)
        osc.start(t)
        osc.stop(t + 1.1)
      },
      rise: () => {
        for (let i = 0; i < 4; i++) this._note(440 + i * 60, t + i * 0.12, 0.3, 'triangle', 0.03, g)
      }
    }
    if (se[name]) se[name]()
  }

  /* 播放配音台词（HTMLAudio，一次性，不循环） */
  playVoice (name) {
    if (!name) return
    const url = VOICE_MAP[name]
    if (!url) return
    if (this.voiceAudio) { try { this.voiceAudio.pause() } catch (e) {} }
    const a = new Audio(url)
    a.volume = 0.9
    a.play().catch(() => {})
    this.voiceAudio = a
  }
}

/* ============================================================
 * 文字颜色解析
 * ------------------------------------------------------------
 * 优先级（从高到低）：
 *   1. 单句 color         story-data.js 里 { type:'text', text:'…', color:'#ff9ecb' }
 *   2. 句内行内标记        '你终于[[#ffd166]]醒[[/]]了。'
 *   3. 角色表 textColor    CHARACTERS[key].textColor
 *   4. 全局 bySpeaker      TEXT_COLORS.bySpeaker[key]
 *   5. 角色标签色（开关）  TEXT_COLORS.useSpeakerColor → CHARACTERS[key].color
 *   6. 全局兜底            TEXT_COLORS.text / TEXT_COLORS.narrator（空则用样式表颜色）
 * 行内标记的写法：[[#色值]]文字[[/]]、[[角色key]]文字[[/]]、[[gold]]文字[[/]]
 * ============================================================ */
const COLOR_CFG = TEXT_COLORS || {}

/* 把 [[…]] 里的内容解析成真实色值：支持 #hex / rgb() / CSS 颜色名 / 角色 key */
function normalizeColor (token) {
  const t = String(token == null ? '' : token).trim()
  if (!t) return ''
  if (/^#[0-9a-fA-F]{3,8}$/.test(t)) return t
  if (/^(rgb|rgba|hsl|hsla)\(/i.test(t)) return t
  const role = CHARACTERS[t]
  if (role) return role.textColor || role.color || ''
  return t
}

/* 把一句原文拆成「纯文本 + 逐字颜色」，行内标记本身不占字位，打字机计数不受影响 */
function richText (raw, baseColor) {
  const text = raw == null ? '' : String(raw)
  const chars = []
  let plain = ''
  let i = 0
  while (i < text.length) {
    if (text[i] === '[' && text[i + 1] === '[') {
      const closeTag = text.indexOf(']]', i + 2)
      /* 残缺的标记（有开头没结尾）按普通文字处理，宁可显示也不吞字 */
      if (closeTag < 0) {
        chars.push({ ch: '[', color: baseColor })
        plain += '['
        i++
        continue
      }
      const end = text.indexOf('[[/]]', closeTag + 2)
      if (end > 0) {
        const color = normalizeColor(text.slice(i + 2, closeTag)) || baseColor
        const inner = text.slice(closeTag + 2, end)
        const list = inner.split('')
        for (let k = 0; k < list.length; k++) {
          chars.push({ ch: list[k], color: color })
          plain += list[k]
        }
        i = end + 5
        continue
      }
      i = closeTag + 2
      continue
    }
    if (text.substr(i, 5) === '[[/]]') { i += 5; continue }
    chars.push({ ch: text[i], color: baseColor })
    plain += text[i]
    i++
  }
  return { plain: plain, chars: chars }
}

/* 一个句子节点最终用什么颜色：返回基础色 + 逐字颜色表 */
function resolveLineStyle (node) {
  let base = node.color || ''
  if (!base) {
    if (node.type === 'text') {
      const key = node.speaker || ''
      let roleKey = CHARACTERS[key] ? key : ''
      /* 兼容 speaker 直接写中文名的情况（按显示名反查一次） */
      if (!roleKey) {
        roleKey = Object.keys(CHARACTERS).find(k => CHARACTERS[k].name === key) || ''
      }
      const role = roleKey ? CHARACTERS[roleKey] : null
      if (roleKey && COLOR_CFG.bySpeaker && COLOR_CFG.bySpeaker[roleKey]) {
        base = COLOR_CFG.bySpeaker[roleKey]
      } else if (role && role.textColor) {
        base = role.textColor
      } else if (role && COLOR_CFG.useSpeakerColor && role.color) {
        base = role.color
      } else {
        base = COLOR_CFG.text || ''
      }
    } else {
      base = COLOR_CFG.narrator || ''
    }
  }
  base = normalizeColor(base)
  const parsed = richText(node.text, base)
  return { color: base, plain: parsed.plain, chars: parsed.chars }
}

/* ============================================================
 * 剧本弹窗单块渲染（函数式组件）
 * ------------------------------------------------------------
 * 原来用 15 个 v-if / v-else-if 逐块判断，1475 块每次重渲染都要跑一遍判断链，
 * 是自动下滑卡顿的主因。改成函数式组件后没有实例、没有响应式依赖，
 * 列表只在打开时创建一次，滚动过程中零重新渲染。
 * ============================================================ */
const SCRIPT_BLOCK_CLASS = {
  'doc-title': 'sc-doc-title',
  'doc-sub': 'sc-doc-sub',
  chapter: 'sc-chapter',
  scene: 'sc-scene',
  bgm: 'sc-sound',
  se: 'sc-sound',
  'choice-title': 'sc-choice-title',
  choice: 'sc-choice',
  merge: 'sc-merge',
  note: 'sc-note',
  dialogue: 'sc-dialogue',
  'table-head': 'sc-table sc-table-head',
  'table-row': 'sc-table sc-table-row'
}

const ScriptBlock = {
  name: 'ScriptBlock',
  functional: true,
  props: {
    b: { type: Object, required: true }
  },
  render (h, ctx) {
    const b = ctx.props.b
    if (b.t === 'dialogue') {
      return h('div', { class: 'sc-dialogue' }, [
        h('span', { class: 'sc-speaker' }, (b.sp || '') + '：'),
        b.s
      ])
    }
    if (b.t === 'table-head' || b.t === 'table-row') {
      const cls = b.t === 'table-head' ? 'sc-table sc-table-head' : 'sc-table sc-table-row'
      return h('div', { class: cls }, (b.cells || []).map(c => h('span', { class: 'sc-tcell' }, c)))
    }
    return h('div', { class: SCRIPT_BLOCK_CLASS[b.t] || 'sc-narrator' }, b.s)
  }
}

export default {
  name: 'LolitaJKHanFu',
  components: {HeaderNav, ScriptBlock},
  data () {
    return {
      STORY,
      CHARACTERS,
      screen: 'title',
      titleBgVideo,
      scriptBgVideo: VIDEO_MAP['森林.mp4'] || '',
      /* 完整剧本浏览弹窗数据与自动下滑状态
       * FULL_SCRIPT 是只读常量：freeze 后 Vue 不再逐项做响应式转换，
       * 1475 个块省掉一次深度遍历，弹窗打开更快 */
      fullScript: Object.freeze(FULL_SCRIPT),
      scriptAuto: false,
      scriptAutoTimer: null,
      scriptAutoDone: false,
      /* 剧情文本打开后先停顿 2 秒再自动下滑（待开始状态） */
      scriptAutoPending: false,
      scriptPendingTimer: null,
      /* 分批渲染：首屏只建 160 块，滚到已渲染底部再续下一批。
       * 一次铺 1848 块要排版 20 万字，会把主线程堵近 1 秒 */
      scriptRendered: 160,
      titlePanel: '',
      galleryView: '',
      navShown: true,
      history: [],
      chapterTimer: null,
      gameFaded: false,
      transActive: false,
      transMedia: null,
      transIsVideo: false,
      transTimers: [],
      stars: [],
      idx: 0,
      curBg: null,
      curCg: null,
      stands: {
        left: { show: false, img: '' },
        center: { show: false, img: '' },
        right: { show: false, img: '' }
      },
      speaker: '',
      namePos: 'left',
      fullText: '',
      shownText: '',
      /* 当前句的基础文字颜色（空 = 用样式表默认色）与逐字颜色表 */
      curTextColor: '',
      curChars: [],
      typing: false,
      typeTimer: null,
      dialogShow: false,
      chapterShow: false,
      chapterTitle: '',
      chapterSubtitle: '',
      optionsShow: false,
      curOptions: [],
      autoMode: false,
      skipMode: false,
      autoTimer: null,
      fxShake: false,
      fxFlash: null,
      flashTimer: null,
      savePanel: false,
      showSetting: false,
      hasSave: false,
      saveSlots: [],
      bgmVol: 60,
      seVol: 80,
      bgmMuted: false,
      textSpeed: 35,
      autoDelay: 3,
      endingLabel: '',
      endingTitle: '',
      endingText: '',
      endingBg: '',
      endingTimer: null,
      endingReturnDelay: 8000,
    }
  },
  computed: {
    /* 剧情文本：当前已渲染的剧本块（分批渲染，避免一次性排版 20 万字） */
    visibleScript () {
      if (this.scriptRendered >= this.fullScript.length) return this.fullScript
      return this.fullScript.slice(0, this.scriptRendered)
    },
    /* 当前已打出的字符（带各自颜色），打字机进度用 shownText.length 控制 */
    shownChars () {
      if (!this.curChars.length) return []
      return this.curChars.slice(0, this.shownText.length)
    },
    /* 场景背景视频：bg 写 videos 目录文件名（带不带扩展名均可）时返回视频 URL */
    curBgVideoUrl () {
      if (!this.curBg) return ''
      if (VIDEO_MAP[this.curBg]) return VIDEO_MAP[this.curBg]
      if (VIDEO_MAP[this.curBg + '.mp4']) return VIDEO_MAP[this.curBg + '.mp4']
      if (VIDEO_MAP[this.curBg + '.webm']) return VIDEO_MAP[this.curBg + '.webm']
      if (VIDEO_MAP[this.curBg + '.ogg']) return VIDEO_MAP[this.curBg + '.ogg']
      return ''
    },
    /* 章节列表（章节选择用） */
    chapters () {
      const list = []
      this.STORY.forEach((n, i) => {
        if (n.type === 'chapter') list.push({ title: n.title, subtitle: n.subtitle || '', idx: i })
      })
      return list
    },
    /* 插画鉴赏：收集剧情中的 CG 插画（去重，标注所在章节） */
    cgList () {
      const seen = {}
      const list = []
      let cur = '序章'
      this.STORY.forEach(n => {
        if (n.type === 'chapter') cur = n.title + (n.subtitle ? ' · ' + n.subtitle : '')
        if (n.type === 'cg' && n.img && !seen[n.img]) {
          seen[n.img] = true
          list.push({ img: n.img, label: cur })
        }
      })
      return list
    },
    /* 剧情文本：按章节分组的全部台词/旁白 */
    scriptGroups () {
      const groups = []
      let cur = null
      this.STORY.forEach((n, i) => {
        if (n.type === 'chapter') {
          cur = { title: n.title, subtitle: n.subtitle || '', lines: [] }
          groups.push(cur)
        } else if ((n.type === 'text' || n.type === 'narrator') && n.text) {
          if (!cur) {
            cur = { title: '序章', subtitle: '', lines: [] }
            groups.push(cur)
          }
          cur.lines.push({
            idx: i,
            speaker: n.type === 'text' ? this.resolveSpeakerName(n.speaker || '') : '',
            text: n.text
          })
        }
      })
      return groups
    },
    /* 继续游戏右侧显示的当前进度 */
    saveProgress () {
      if (!this.hasSave) return '暂无存档，开始一段新的旅程吧'
      let last = -1
      for (let i = 0; i < 6; i++) if (this.saveSlots[i]) last = i
      const s = this.saveSlots[last]
      const pct = Math.min(99, Math.round((s.idx / this.STORY.length) * 100))
      return '当前进度：' + (s.chapter || '已保存') + ' · ' + pct + '%'
    },
    /* 剧情文本弹窗顶部提示：随自动下滑状态变化 */
    scriptTipText () {
      if (this.scriptAutoPending) return '即将自动下滑 · 点击「暂停」可手动浏览'
      if (this.scriptAuto) return '自动下滑中 · 点击「暂停」可手动浏览'
      if (this.scriptAutoDone) return '已浏览至结尾 · 可手动滚动回顾'
      return '已暂停 · 自由滚动浏览，点击「继续」恢复自动下滑'
    }
  },
  created () {
    const arr = []
    for (let i = 0; i < 60; i++) {
      arr.push({
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: Math.random() * 3 + 1,
        delay: Math.random() * 6,
        dur: Math.random() * 5 + 4,
        opacity: Math.random() * 0.7 + 0.3
      })
    }
    this.stars = arr
    try {
      const s = JSON.parse(localStorage.getItem('lolita_vn_settings') || '{}')
      if (s.bgmVol !== undefined) this.bgmVol = s.bgmVol
      if (s.seVol !== undefined) this.seVol = s.seVol
      if (s.bgmMuted !== undefined) this.bgmMuted = !!s.bgmMuted
      if (s.textSpeed !== undefined) this.textSpeed = s.textSpeed
      if (s.autoDelay !== undefined) this.autoDelay = s.autoDelay
    } catch (e) { /* 忽略设置解析错误 */ }
    const slots = JSON.parse(localStorage.getItem('lolita_vn_saves') || '{}')
    this.saveSlots = Array.from({ length: 6 }, (_, i) => slots[i] || null)
    this.hasSave = this.saveSlots.some(Boolean)
  },
  mounted () {
    this.audio = new AudioEngine()
    this.audio.init()
    const resumeOnce = () => {
      this.audio.resume()
      document.removeEventListener('pointerdown', resumeOnce)
    }
    document.addEventListener('pointerdown', resumeOnce)
    if (this.screen === 'title') this.audio.playBgm('music_4.mp3')
    if (this.bgmMuted) this.audio.muteBgm(true)
    /* 跟随自动隐藏导航栏：导航栏展开时系统栏下移避让 */
    bus.$on('nav-hidden-change', this.onNavHiddenChange)
  },
  beforeDestroy () {
    bus.$off('nav-hidden-change', this.onNavHiddenChange)
    if (this.typeTimer) clearTimeout(this.typeTimer)
    if (this.autoTimer) clearTimeout(this.autoTimer)
    if (this.flashTimer) clearTimeout(this.flashTimer)
    if (this.chapterTimer) clearTimeout(this.chapterTimer)
    if (this.endingTimer) clearTimeout(this.endingTimer)
    this.transTimers.forEach(t => clearTimeout(t))
    this.stopScriptAuto()
    if (this.audio) this.audio._stopBgm()
  },
  methods: {
    /* ---------- 剧情文本弹窗：自动下滑 / 暂停切换 ---------- */
    startScriptAuto () {
      if (this.scriptAutoTimer) return
      const list = this.$refs.scriptListRef
      if (!list) return
      this.scriptAuto = true
      this.scriptAutoDone = false
      /* 时间基准滚动：原来「每帧固定 +5px」在掉帧时会变慢、在 120Hz 屏上又变快，
       * 现在按 300px/s 换算（与原 60fps 下 +5px/帧 等价），快慢与刷新率无关 */
      const speed = 300
      let last = 0
      let acc = 0
      const step = now => {
        if (!this.scriptAuto || !this.$refs.scriptListRef) {
          this.scriptAutoTimer = null
          return
        }
        if (!last) last = now
        /* 单帧最多补 64ms，避免切后台回来时一次跳很远 */
        const dt = Math.min(now - last, 64)
        last = now
        acc += speed * dt / 1000
        const px = Math.floor(acc)
        if (px > 0) {
          acc -= px
          list.scrollTop += px
        }
        /* 到底判断：已渲染部分滚到底时，先续下一批；全部渲染完才算到达结尾 */
        if (list.scrollTop + list.clientHeight >= list.scrollHeight - 1) {
          if (this.scriptRendered < this.fullScript.length) {
            this.scriptRendered = Math.min(this.fullScript.length, this.scriptRendered + 60)
            this.scriptAutoTimer = requestAnimationFrame(step)
            return
          }
          this.scriptAuto = false
          this.scriptAutoDone = true
          this.scriptAutoTimer = null
          return
        }
        this.scriptAutoTimer = requestAnimationFrame(step)
      }
      this.scriptAutoTimer = requestAnimationFrame(step)
    },
    stopScriptAuto () {
      if (this.scriptAutoTimer) cancelAnimationFrame(this.scriptAutoTimer)
      this.scriptAutoTimer = null
      this.scriptAuto = false
      this.clearScriptPending()
    },
    /* 取消「待开始」状态的自动下滑（停顿等待期间用户介入 / 关闭弹窗时） */
    clearScriptPending () {
      if (this.scriptPendingTimer) clearTimeout(this.scriptPendingTimer)
      this.scriptPendingTimer = null
      this.scriptAutoPending = false
    },
    toggleScriptAuto () {
      this.audio.playSe('click')
      if (this.scriptAuto) {
        this.stopScriptAuto()
      } else if (this.scriptAutoPending) {
        /* 停顿等待期间点击：取消待开始的自动下滑，切换为手动浏览 */
        this.clearScriptPending()
      } else {
        this.scriptAutoDone = false
        this.startScriptAuto()
      }
    },
    /* 用户手动介入（滚轮 / 拖动滚动条 / 触屏）时自动暂停自动下滑 */
    onScriptUserScroll () {
      if (this.scriptAuto) {
        this.stopScriptAuto()
        return
      }
      if (this.scriptAutoPending) this.clearScriptPending()
    },
    /* 实际发生滚动（滚轮 / 拖动滚动条 / 键盘 / 触屏都算）：
     * 只负责分批续渲染，背景视频保持常驻播放，不做暂停 */
    onScriptScroll () {
      /* 拖到已渲染部分的底部：续下一批，实现「越读越长」的分批加载 */
      if (this.scriptRendered < this.fullScript.length) {
        const list = this.$refs.scriptListRef
        if (list && list.scrollTop + list.clientHeight >= list.scrollHeight - 240) {
          this.scriptRendered = Math.min(this.fullScript.length, this.scriptRendered + 60)
        }
      }
    },
    /* ---------- 导航栏联动：展开时系统栏下移避让 ---------- */
    onNavHiddenChange (hidden) {
      this.navShown = !hidden
    },
    /* 解析过渡媒体：以 .mp4/.webm/.ogg 结尾视为 videos 目录视频，否则视为 story-img 图片 */
    resolveTransitionMedia (name) {
      if (!name) return { url: '', isVideo: false }
      if (/\.(mp4|webm|ogg)$/i.test(name)) {
        return { url: VIDEO_MAP[name] || name, isVideo: true }
      }
      return { url: this.img(name), isVideo: false }
    },
    /* ---------- 资源与角色解析 ---------- */
    /* 图片查找：直接用文件名（带或不带扩展名均可），自动匹配 .png/.jpg/.jpeg/.gif */
    img (name) {
      if (!name) return ''
      if (IMG_MAP[name]) return IMG_MAP[name]
      // 不带扩展名时自动尝试补全
      if (!/\.(png|jpe?g|gif)$/i.test(name)) {
        for (const ext of ['.png', '.jpg', '.jpeg', '.gif']) {
          if (IMG_MAP[name + ext]) return IMG_MAP[name + ext]
        }
      }
      return ''
    },
    /* 立绘引用解析：'buluo' → 默认立绘；'buluo:serious' → 表情；否则视为图片文件名 */
    resolveStand (ref) {
      if (!ref) return ''
      if (ref.indexOf(':') > 0) {
        const [key, expr] = ref.split(':')
        const c = CHARACTERS[key]
        if (c && c.expressions && c.expressions[expr]) return c.expressions[expr]
        if (c) return c.stand
        return expr + '.jpg'
      }
      if (CHARACTERS[ref]) return CHARACTERS[ref].stand
      return ref
    },
    resolveSpeakerName (s) {
      if (!s) return ''
      return CHARACTERS[s] ? CHARACTERS[s].name : s
    },
    /* 姓名框位置推断：说话人立绘在左→姓名框左、在右→右、在中间→中间（未显式写 namePos 时生效） */
    resolveNamePos (speaker, stands) {
      if (!stands) return 'left'
      let key = speaker
      if (speaker && !CHARACTERS[speaker]) {
        const found = Object.keys(CHARACTERS).find(k => CHARACTERS[k].name === speaker)
        if (found) key = found
      }
      const posList = ['left', 'center', 'right']
      for (const pos of posList) {
        const v = stands[pos]
        if (!v) continue
        const base = String(v).split(':')[0]
        if (base === key) return pos
        if (key && CHARACTERS[base] && CHARACTERS[key] && CHARACTERS[base].name === CHARACTERS[key].name) return pos
      }
      return 'left'
    },
    speakerColor (s) {
      const key = Object.keys(CHARACTERS).find(k => CHARACTERS[k].name === s || k === s)
      return (key && CHARACTERS[key].color) || '#9aa0a6'
    },
    /* 应用三位置立绘配置：未配置的位置 → 无立绘 */
    applyStands (standsCfg) {
      const posList = ['left', 'center', 'right']
      posList.forEach(pos => {
        const v = standsCfg ? standsCfg[pos] : null
        if (v) {
          this.stands[pos] = { show: true, img: this.resolveStand(v) }
        } else {
          this.stands[pos] = { show: false, img: '' }
        }
      })
    },
    starStyle (s) {
      return {
        left: s.left + '%',
        top: s.top + '%',
        width: s.size + 'px',
        height: s.size + 'px',
        animationDelay: s.delay + 's',
        animationDuration: s.dur + 's',
        opacity: s.opacity
      }
    },
    /* ---------- 标题 ---------- */
    startNew () {
      this.audio.playSe('click')
      this.audio.playBgm('lab')
      this.resetGame()
      this.history = []
      this.screen = 'game'
      this.idx = 0
      this.processNode()
    },
    loadGame () {
      if (!this.hasSave) return
      this.audio.playSe('click')
      this.audio.playBgm('lab')
      const slots = JSON.parse(localStorage.getItem('lolita_vn_saves') || '{}')
      let last = -1
      for (let i = 0; i < 6; i++) if (slots[i]) last = i
      if (last < 0) return
      this.restoreFromSave(slots[last])
    },
    openSave () {
      this.audio.playSe('click')
      this.savePanel = 'save'
    },
    openLoad () {
      this.audio.playSe('click')
      this.savePanel = 'load'
    },
    slotAction (n) {
      if (this.savePanel === 'save') {
        const slots = JSON.parse(localStorage.getItem('lolita_vn_saves') || '{}')
        slots[n] = {
          idx: this.idx,
          chapter: this.currentChapterLabel(),
          time: new Date().toLocaleString('zh-CN', { hour12: false }).slice(5, 16)
        }
        localStorage.setItem('lolita_vn_saves', JSON.stringify(slots))
        this.saveSlots = Array.from({ length: 6 }, (_, i) => slots[i] || null)
        this.hasSave = true
        this.audio.playSe('click')
      } else {
        const slots = JSON.parse(localStorage.getItem('lolita_vn_saves') || '{}')
        if (!slots[n]) return
        this.audio.playSe('click')
        this.savePanel = false
        this.titlePanel = ''
        this.restoreFromSave(slots[n])
      }
    },
    restoreFromSave (save) {
      this.resetGame()
      this.history = []
      this.idx = save.idx
      this.screen = 'game'
      let i = save.idx
      while (i >= 0) {
        const n = this.STORY[i]
        if (n.type === 'scene' && n.bg) { this.curBg = n.bg; break }
        if (n.type === 'chapter') { break }
        i--
      }
      this.processNode()
    },
    currentChapterLabel () {
      let label = '序章'
      for (let i = 0; i <= this.idx; i++) {
        const n = this.STORY[i]
        if (n.type === 'chapter') label = n.title + ' · ' + (n.subtitle || '')
      }
      return label
    },
    openSetting () {
      this.audio.playSe('click')
      this.showSetting = true
    },
    closeSetting () {
      this.showSetting = false
      this.persistSettings()
    },
    /* ---------- 标题页功能菜单 ---------- */
    openTitlePanel (name) {
      this.audio.playSe('click')
      if (name === 'load') this.savePanel = 'load'
      this.titlePanel = name
      /* 剧情文本弹窗：从顶部开始，停顿 2 秒后再自动下滑 */
      if (name === 'script') {
        this.stopScriptAuto()
        this.scriptAutoDone = false
        this.scriptRendered = 160
        this.$nextTick(() => {
          const el = this.$refs.scriptListRef
          if (el) el.scrollTop = 0
          /* 先停顿 2 秒，让用户看到开头文本后再自动下滑 */
          this.scriptAutoPending = true
          this.scriptPendingTimer = setTimeout(() => {
            this.scriptAutoPending = false
            this.scriptPendingTimer = null
            this.startScriptAuto()
          }, 2000)
        })
      }
    },
    closeTitlePanel () {
      if (this.titlePanel === 'setting') this.persistSettings()
      if (this.titlePanel === 'script') this.stopScriptAuto()
      this.savePanel = false
      this.titlePanel = ''
    },
    gotoChapter (ci) {
      const ch = this.chapters[ci]
      if (!ch) return
      this.audio.playSe('click')
      this.audio.playBgm('lab')
      this.closeTitlePanel()
      this.resetGame()
      this.history = []
      this.idx = ch.idx
      this.screen = 'game'
      this.processNode()
    },
    jumpToLine (idx) {
      this.audio.playSe('click')
      this.audio.playBgm('lab')
      this.closeTitlePanel()
      this.resetGame()
      this.history = []
      this.idx = idx
      /* 回退找到最近的场景背景，避免直接跳到中段台词时黑屏 */
      let i = idx
      while (i >= 0) {
        const n = this.STORY[i]
        if (n.type === 'scene' && n.bg) { this.curBg = n.bg; break }
        if (n.type === 'chapter') break
        i--
      }
      this.screen = 'game'
      this.processNode()
    },
    applyVolumes () {
      if (this.audio) this.audio.setVolumes(this.bgmVol / 100, this.seVol / 100)
      this.persistSettings()
    },
    toggleMute () {
      this.bgmMuted = !this.bgmMuted
      if (this.audio) this.audio.muteBgm(this.bgmMuted)
      this.persistSettings()
    },
    persistSettings () {
      localStorage.setItem('lolita_vn_settings', JSON.stringify({
        bgmVol: this.bgmVol,
        seVol: this.seVol,
        bgmMuted: this.bgmMuted,
        textSpeed: this.textSpeed,
        autoDelay: this.autoDelay
      }))
    },
    backToTitle () {
      this.audio.playSe('click')
      this.audio.playBgm('music_4.mp3')
      this.screen = 'title'
      this.resetGame()
      this.history = []
    },
    resetGame () {
      if (this.typeTimer) clearTimeout(this.typeTimer)
      if (this.autoTimer) clearTimeout(this.autoTimer)
      if (this.chapterTimer) { clearTimeout(this.chapterTimer); this.chapterTimer = null }
      if (this.endingTimer) { clearTimeout(this.endingTimer); this.endingTimer = null }
      this.transTimers.forEach(t => clearTimeout(t))
      this.transTimers = []
      this.gameFaded = false
      this.transActive = false
      this.transMedia = null
      this.transIsVideo = false
      this.curBg = null
      this.curCg = null
      this.stands = {
        left: { show: false, img: '' },
        center: { show: false, img: '' },
        right: { show: false, img: '' }
      }
      this.speaker = ''
      this.fullText = ''
      this.shownText = ''
      this.curTextColor = ''
      this.curChars = []
      this.typing = false
      this.dialogShow = false
      this.chapterShow = false
      this.optionsShow = false
      this.curOptions = []
      this.autoMode = false
      this.skipMode = false
    },
    /* ---------- 推进 ---------- */
    onClick () {
      if (this.gameFaded) return
      if (this.optionsShow || this.chapterShow) return
      if (this.typing) {
        this.finishTyping()
        return
      }
      this.advance()
    },
    advance () {
      this.idx++
      this.processNode()
    },
    /* ---------- 退回上一句 ---------- */
    goBackLine () {
      /* 弹出当前节点及其后的非台词节点，再向前找最近的一句台词/旁白 */
      const cur = this.idx
      while (this.history.length && this.history[this.history.length - 1] === cur) this.history.pop()
      let target = -1
      let targetPos = -1
      for (let k = this.history.length - 1; k >= 0; k--) {
        const idx = this.history[k]
        const n = this.STORY[idx]
        if (n.type === 'text' || n.type === 'narrator') {
          target = idx
          targetPos = k
          break
        }
      }
      if (target < 0 || target >= this.idx) return
      const sliced = this.history.slice(0, targetPos + 1)
      this.restoreToNode(target)
      this.history = sliced
    },
    /* 恢复到指定节点：清空画面状态后重建背景/立绘/CG，再处理目标节点 */
    restoreToNode (idx) {
      this.audio.playSe('click')
      this.resetGame()
      this.idx = idx
      this.screen = 'game'
      this.simulateUpTo(idx)
      this.processNode()
    },
    /* 静默重建目标节点之前的场景状态（不触发音效/特效/定时器） */
    simulateUpTo (idx) {
      for (let k = 0; k < idx; k++) {
        const n = this.STORY[k]
        switch (n.type) {
          case 'scene':
            if (n.bg) this.curBg = n.bg
            this.curCg = null
            this.applyStands(null)
            break
          case 'cg':
            this.curCg = n.img || null
            break
          case 'text':
          case 'narrator':
            if (n.bg) { this.curBg = n.bg; this.curCg = null }
            if (n.stands !== undefined) {
              this.applyStands(n.stands)
            } else if (n.type === 'narrator') {
              this.applyStands(null)
            }
            break
          case 'chapter':
            this.chapterTitle = n.title
            this.chapterSubtitle = n.subtitle || ''
            break
          default:
            break
        }
      }
    },
    processNode () {
      /* 记录走过的节点（供「退回上一句」沿实际路径回退） */
      if (this.history[this.history.length - 1] !== this.idx) this.history.push(this.idx)
      if (this.idx >= this.STORY.length) {
        this.backToTitle()
        return
      }
      const n = this.STORY[this.idx]
      switch (n.type) {
        case 'label':
          this.advance()
          break
        case 'jump': {
          /* 分支跳转：跳到指定 label 继续（用于选项分支汇合回主线） */
          const idx = this.STORY.findIndex(x => x.type === 'label' && x.id === n.goto)
          if (idx >= 0) {
            this.idx = idx
            this.processNode()
          } else {
            this.advance()
          }
          break
        }
        case 'chapter':
          this.dialogShow = false
          this.optionsShow = false
          this.chapterTitle = n.title
          this.chapterSubtitle = n.subtitle || ''
          this.chapterShow = true
          this.audio.playSe('rise')
          if (this.chapterTimer) clearTimeout(this.chapterTimer)
          this.chapterTimer = setTimeout(() => {
            if (this.screen !== 'game') return
            this.chapterShow = false
            this.advance()
          }, 2200)
          break
        case 'scene':
          if (n.bg) this.curBg = n.bg
          this.curCg = null
          /* 切换场景时清空上一场景残留的立绘（新场景如需人物，由后续节点重新布置） */
          this.applyStands(null)
          this.dialogShow = false
          this.optionsShow = false
          this.advance()
          break
        case 'transition': {
          /* 场景过渡：整个页面淡出 → 过渡图片/视频淡入停留（默认 5s）→ 淡出 → 新剧情淡入 */
          this.gameFaded = true
          this.dialogShow = false
          this.optionsShow = false
          const dur = n.duration || 5000
          const media = this.resolveTransitionMedia(n.media)
          const t1 = setTimeout(() => {
            if (this.screen !== 'game') return
            this.transMedia = media.url
            this.transIsVideo = media.isVideo
            this.transActive = true
            const t2 = setTimeout(() => {
              if (this.screen !== 'game') return
              this.transActive = false
              const t3 = setTimeout(() => {
                if (this.screen !== 'game') return
                this.gameFaded = false
                this.transMedia = null
                this.advance()
              }, 900)
              this.transTimers.push(t3)
            }, dur)
            this.transTimers.push(t2)
          }, 800)
          this.transTimers.push(t1)
          break
        }
        case 'cg':
          this.curCg = n.img || null
          if (n.img) this.dialogShow = false
          this.advance()
          break
        case 'text':
        case 'narrator':
          this.speaker = n.type === 'text' ? this.resolveSpeakerName(n.speaker || '') : ''
          this.namePos = n.namePos || this.resolveNamePos(n.speaker || '', n.stands)
          /* 逐句着色：单句 color > 行内 [[…]] > 角色 textColor > 全局默认 */
          const styled = resolveLineStyle(n)
          this.curTextColor = styled.color
          this.curChars = styled.chars
          this.fullText = styled.plain
          this.shownText = ''
          this.dialogShow = true
          this.optionsShow = false
          if (n.bg) { this.curBg = n.bg; this.curCg = null }
          if (n.stands !== undefined) {
            this.applyStands(n.stands)
          } else if (n.type === 'narrator') {
            /* 旁白未指定 stands：清除所有立绘（与 story-data 文档约定一致） */
            this.applyStands(null)
          }
          if (n.se) this.audio.playSe(n.se)
          if (n.voice) this.audio.playVoice(n.voice)
          if (n.bgm) this.audio.playBgm(n.bgm)
          else if (n.bgmStop) this.audio._stopBgm()
          this.startTyping()
          break
        case 'se':
          this.audio.playSe(n.name)
          this.advance()
          break
        case 'bgm':
          if (n.stop) {
            this.audio._stopBgm()
          } else if (n.file) {
            this.audio.playBgm(n.file)
          } else {
            this.audio.playBgm(n.name)
          }
          this.advance()
          break
        case 'effect':
          if (n.shake) {
            this.fxShake = true
            setTimeout(() => { this.fxShake = false }, 700)
          }
          if (n.flash) {
            this.fxFlash = n.flash
            if (this.flashTimer) clearTimeout(this.flashTimer)
            this.flashTimer = setTimeout(() => { this.fxFlash = null }, 500)
          }
          this.advance()
          break
        case 'option':
          this.dialogShow = false
          this.optionsShow = true
          this.curOptions = n.choices
          break
        case 'save':
          {
            const slots = JSON.parse(localStorage.getItem('lolita_vn_saves') || '{}')
            if (!slots[0] || slots[0].idx !== this.idx) {
              slots[0] = { idx: this.idx, chapter: this.currentChapterLabel(), time: new Date().toLocaleString('zh-CN', { hour12: false }).slice(5, 16), auto: true }
              localStorage.setItem('lolita_vn_saves', JSON.stringify(slots))
              this.saveSlots = Array.from({ length: 6 }, (_, i) => slots[i] || null)
              this.hasSave = true
            }
          }
          this.advance()
          break
        case 'ending':
          this.endingLabel = n.kind === 'bad' ? 'BAD END' : (n.kind === 'fan' ? '番外 · END' : 'GOOD END')
          this.endingTitle = n.title
          this.endingText = n.text
          this.endingBg = this.img(n.bg) || this.img((n.bg || 'title') + '.jpg')
          this.screen = 'ending'
          this.audio.playBgm('music_4.mp3')
          this.audio.playSe(n.kind === 'bad' ? 'alarm' : 'rise')
          /* 剧情结束：停留片刻后自动返回标题页（可点击按钮立即返回） */
          if (this.endingTimer) clearTimeout(this.endingTimer)
          this.endingTimer = setTimeout(() => {
            if (this.screen === 'ending') this.backToTitle()
          }, this.endingReturnDelay)
          break
        default:
          this.advance()
      }
    },
    startTyping () {
      this.typing = true
      // 速度映射：textSpeed 10=最慢(近逐字) / 35=默认舒适 / 100=最快
      const step = this.skipMode ? 1 : Math.max(1, Math.round(this.textSpeed / 20))
      this.typeTimer = setInterval(() => {
        if (this.shownText.length < this.fullText.length) {
          this.shownText = this.fullText.slice(0, this.shownText.length + step)
          if (this.shownText.length % 3 === 0 && this.seVol > 0) this.audio.playSe('type')
        } else {
          this.finishTyping()
        }
      }, this.skipMode ? 5 : 60)
    },
    finishTyping () {
      if (this.typeTimer) clearInterval(this.typeTimer)
      this.typeTimer = null
      this.typing = false
      this.shownText = this.fullText
      if (this.autoMode) this.scheduleAuto()
    },
    scheduleAuto () {
      if (this.autoTimer) clearTimeout(this.autoTimer)
      this.autoTimer = setTimeout(() => {
        if (!this.autoMode || this.screen !== 'game') return
        if (this.optionsShow) return
        this.advance()
      }, this.autoDelay * 1000)
    },
    chooseOption (i) {
      const c = this.curOptions[i]
      this.audio.playSe('click')
      this.optionsShow = false
      this.curOptions = []
      const idx = this.STORY.findIndex(x => x.type === 'label' && x.id === c.goto)
      if (idx >= 0) {
        this.idx = idx
        this.processNode()
      }
    },
    toggleAuto () {
      this.audio.playSe('click')
      this.autoMode = !this.autoMode
      if (this.autoMode) this.skipMode = false
      if (this.autoMode && !this.typing && this.dialogShow) this.scheduleAuto()
      if (!this.autoMode && this.autoTimer) { clearTimeout(this.autoTimer); this.autoTimer = null }
    },
    toggleSkip () {
      this.audio.playSe('click')
      this.skipMode = !this.skipMode
      if (this.skipMode) this.autoMode = false
      if (this.skipMode && this.typing) {
        if (this.typeTimer) clearInterval(this.typeTimer)
        this.typeTimer = setInterval(() => {
          if (this.shownText.length < this.fullText.length) {
            this.shownText = this.fullText.slice(0, this.shownText.length + 2)
          } else {
            this.finishTyping()
          }
        }, 5)
      }
    }
  }
}
</script>

<style scoped>
/* 剧情文本字体：font 文件夹内的 ttf（找不到时回退系统字体） */
@font-face {
  font-family: 'TianShangXingXing';
  src: url('./font/天上一只小星星-fixed-repaired.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
  font-display: swap;
}
.vn-root {
  position: relative;
  width: 100%;
  height: 100vh;
  min-height: 620px;
  background: #0b0d17;
  overflow: hidden;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
  user-select: none;
}

/* ================= 标题画面 ================= */
.vn-title {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.title-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  object-fit: cover;
  animation: slowZoom 24s ease-in-out infinite alternate;
}
@keyframes slowZoom {
  from { transform: scale(1); }
  to { transform: scale(1.08); }
}
.star-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.star {
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(circle, #fff 0%, rgba(255,255,255,0) 70%);
  animation: twinkle 3s ease-in-out infinite;
}
@keyframes twinkle {
  0%, 100% { opacity: 0.15; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.2); }
}
.title-vignette {
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at center, rgba(10,8,26,0.1) 0%, rgba(10,8,26,0.55) 78%, rgba(5,4,14,0.85) 100%);
}
.title-content {
  position: relative;
  z-index: 2;
  text-align: center;
  color: #f8f2ff;
  text-shadow: 0 2px 18px rgba(255, 130, 200, 0.4);
}
.title-sub {
  font-size: 15px;
  letter-spacing: 6px;
  color: #ffb8dd;
  margin-bottom: 14px;
}
.title-main {
  font-size: 72px;
  font-weight: 700;
  letter-spacing: 18px;
  margin: 0;
  background: linear-gradient(120deg, #ff9ecb 20%, #ffd7ec 45%, #c9a8ff 75%, #ff9ecb 95%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: titleShine 5s linear infinite;
}
@keyframes titleShine {
  to { background-position: 200% center; }
}
.title-sub2 {
  font-size: 30px;
  font-weight: 500;
  letter-spacing: 14px;
  margin: 6px 0 28px;
  color: #ffe4f2;
}
.title-btns {
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: center;
}
/* 标题页按钮区：两列网格，继续游戏与开始新游戏通栏 */
.vn-title .title-btns {
  display: grid;
  grid-template-columns: repeat(2, 250px);
  gap: 12px 18px;
  justify-content: center;
}
.vn-title .title-btns .vn-btn {
  width: 100%;
}
.title-btns .wide {
  grid-column: 1 / -1;
}
.continue-row {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
}
.continue-row .vn-btn {
  width: 220px;
}
.progress-tag {
  font-size: 13px;
  letter-spacing: 1px;
  color: #ffd7ec;
  background: rgba(30, 20, 48, 0.35);
  border: 1px solid rgba(255, 190, 222, 0.35);
  border-radius: 15px;
  padding: 9px 16px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  white-space: nowrap;
}
.vn-btn {
  min-width: 220px;
  padding: 12px 30px;
  border: 1px solid rgba(255, 190, 222, 0.45);
  background: rgba(34, 22, 50, 0.3);
  color: #ffe6f4;
  font-size: 16px;
  letter-spacing: 4px;
  border-radius: 15px;
  cursor: pointer;
  transition: all 0.25s;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}
.vn-btn.disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.vn-btn.disabled:hover {
  background: rgba(34, 22, 50, 0.3);
  border-color: rgba(255, 190, 222, 0.45);
  transform: none;
  box-shadow: none;
}
.vn-btn:hover {
  background: rgba(255, 150, 200, 0.28);
  border-color: #ff9ecb;
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(255, 130, 200, 0.35);
}
.vn-btn.main {
  background: linear-gradient(120deg, #ff7fb6, #c48bff);
  border: none;
  color: #fff;
  box-shadow: 0 8px 28px rgba(255, 110, 180, 0.45);
}
.vn-btn.small {
  min-width: 120px;
  font-size: 14px;
  padding: 9px 22px;
  margin-top: 14px;
}
/* 静音按钮状态：静音时按钮暗化并显示斜杠线 */
.vn-btn.muted {
  opacity: 0.55;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.25);
}
.title-tip {
  margin-top: 26px;
  font-size: 12px;
  letter-spacing: 2px;
  color: rgba(255, 235, 248, 0.55);
}
.ending-auto-hint {
  margin-top: 18px;
  font-size: 12px;
  letter-spacing: 2px;
  color: rgba(255, 235, 248, 0.5);
  animation: blink 1.6s infinite;
}
/* 结局画面按钮保持原竖向布局 */
.ending-screen .title-btns {
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: center;
}

/* ================= 标题页功能面板 ================= */
/* 章节选择 */
.chapter-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 46vh;
  overflow-y: auto;
  padding: 4px;
}
.chapter-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
  min-width: 300px;
}
.chapter-item .ch-title {
  font-size: 16px;
  letter-spacing: 3px;
}
.chapter-item .ch-sub {
  font-size: 12px;
  color: rgba(255, 214, 236, 0.75);
  letter-spacing: 2px;
}
/* 插画鉴赏 */
.gallery-card {
  min-width: 560px;
  max-width: 92%;
}
.gallery-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  max-height: 52vh;
  overflow-y: auto;
  padding: 4px;
}
.gallery-item {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid rgba(255, 160, 210, 0.3);
}
.gallery-item img {
  width: 100%;
  height: 180px;
  object-fit: cover;
  display: block;
  transition: transform 0.3s;
}
.gallery-item:hover img {
  transform: scale(1.04);
}
.gallery-label {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 6px 10px;
  font-size: 12px;
  letter-spacing: 1px;
  color: #ffe6f4;
  background: linear-gradient(transparent, rgba(10, 8, 20, 0.85));
}
.gallery-view {
  flex-direction: column;
}
.gallery-view img {
  max-width: 86vw;
  max-height: 78vh;
  border-radius: 12px;
  box-shadow: 0 20px 70px rgba(0, 0, 0, 0.7);
}
.gallery-view .vn-btn {
  margin-top: 18px;
}
/* 剧情文本弹窗：打开淡入 / 关闭淡出 */
.vn-modal-fade-enter-active,
.vn-modal-fade-leave-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}
.vn-modal-fade-enter,
.vn-modal-fade-leave-to {
  opacity: 0;
  transform: scale(0.98);
}
/* 剧情文本（全屏弹窗卡片背景：森林视频） */
.script-modal {
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}
.script-card {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  min-width: 0;
  max-width: none;
  padding: 0;
  border: none;
  border-radius: 0;
  overflow: hidden;
}
.script-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}
/* 视频上叠加轻量暗色，保证台词文字可读 */
.script-card::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 0;
  background: rgba(8, 6, 16, 0.15);
  pointer-events: none;
}
.script-body {
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 15px 0;
  box-sizing: border-box;
}
.script-body h3 {
  font-size: 28px;
  letter-spacing: 6px;
}
.script-tip {
  font-size: 16px;
  letter-spacing: 1px;
  color: rgba(255, 214, 236, 0.6);
  margin-bottom: 14px;
}
.script-list {
  flex: 1;
  max-height: none;
  font-size: 26px;
  overflow-y: auto;
  overflow-x: hidden;
  text-align: left;
  padding: 4px 6px 4px 15px;
  margin-bottom: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  /* 提示合成器优先优化滚动，减少滚动时的重绘抖动 */
  will-change: scroll-position;
}
/* 每个剧本块独立布局/样式，避免整篇 20 万字互相触发重排 */
.script-list > * {
  contain: layout style;
}
/* ================= 完整剧本浏览：右上角暂停/关闭工具条 ================= */
.script-tools {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 10px;
  /* 与导航栏滑动保持同步（导航栏滑入/滑出时长一致） */
  transition: top 0.55s cubic-bezier(0.4, 0, 0.2, 1);
}
/* 导航栏展开时，工具条随之下移到导航栏下方（导航栏高度 60px + 12px 间距），不被遮挡 */
.script-tools.script-tools-under-nav {
  top: 72px;
}
/* 工具条内的关闭按钮改为普通流内元素，与暂停按钮并排，避免 absolute 重叠 */
.script-tools .vn-modal-close {
  position: static;
}
.script-pause-btn {
  height: 32px;
  padding: 0 16px;
  border: 1px solid rgba(255, 160, 210, 0.45);
  background: rgba(34, 22, 50, 0.55);
  color: #ffd7ec;
  font-size: 14px;
  letter-spacing: 1px;
  border-radius: 18px;
  cursor: pointer;
  transition: all 0.2s;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}
.script-pause-btn:hover {
  background: rgba(255, 140, 195, 0.4);
  border-color: #ff9ecb;
  color: #fff;
}
/* ================= 完整剧本浏览：按类型着色的排版 ================= */
.sc-doc-title {
  text-align: center;
  font-size: 36px;
  font-weight: 700;
  letter-spacing: 4px;
  margin: 24px 0 6px;
  background: linear-gradient(120deg, #ff9ecb 20%, #ffd7ec 45%, #c9a8ff 75%, #ff9ecb 95%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  /* 原来这里有一个 5s 循环的流光动画：background-clip:text 每帧都要重绘文字，
   * 是长文滚动掉帧的固定开销，这里改成静态渐变（想恢复可加回
   * animation: titleShine 5s linear infinite） */
  background-position: 0 center;
}
.sc-doc-sub {
  text-align: center;
  font-size: 20px;
  letter-spacing: 4px;
  color: #ffb8dd;
  margin: 0 0 22px;
}
.sc-chapter {
  text-align: center;
  font-size: 30px;
  font-weight: 700;
  letter-spacing: 6px;
  color: #c9a8ff;
  margin: 26px 0 10px;
  padding: 12px 8px 10px;
  border-top: 1px solid rgba(201, 168, 255, 0.35);
  border-bottom: 1px solid rgba(201, 168, 255, 0.35);
  background: linear-gradient(180deg, rgba(201, 168, 255, 0.12), rgba(201, 168, 255, 0.03));
  border-radius: 10px;
  text-shadow: 0 0 18px rgba(201, 168, 255, 0.45);
}
/* 地点与时间：淡蓝色 */
.sc-scene {
  font-size: 20px;
  letter-spacing: 1px;
  color: #8ecdf5;
  font-style: italic;
  margin: 10px 0 2px;
  padding: 6px 10px;
  border-left: 3px solid rgba(142, 205, 245, 0.5);
  background: rgba(142, 205, 245, 0.08);
  border-radius: 0 8px 8px 0;
}
/* BGM / SE：灰绿色 */
.sc-sound {
  font-size: 18px;
  letter-spacing: 1px;
  color: #9fd8b0;
  font-style: italic;
  margin: 2px 0;
  opacity: 0.9;
}
/* 选择点标题：橙色 */
.sc-choice-title {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #ffb27d;
  margin: 18px 0 4px;
  padding: 8px 10px;
  border: 1px dashed rgba(255, 178, 125, 0.45);
  border-radius: 10px;
  background: rgba(255, 178, 125, 0.1);
  text-align: center;
}
/* 选项 A / B：金色 */
.sc-choice {
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 1px;
  color: #ffd98a;
  margin: 2px 0;
  padding: 6px 14px;
  border-left: 4px solid rgba(255, 217, 138, 0.6);
  background: rgba(255, 217, 138, 0.08);
  border-radius: 0 8px 8px 0;
}
/* 汇合 / 转场 / 分支结束：淡青色 */
.sc-merge {
  text-align: center;
  font-size: 20px;
  letter-spacing: 2px;
  color: #8fe3dd;
  margin: 14px 0;
  padding: 6px 8px;
  background: rgba(143, 227, 221, 0.08);
  border-radius: 8px;
}
/* 舞台说明（括号内）：灰色斜体 */
.sc-note {
  font-size: 20px;
  line-height: 1.7;
  color: #b8b2c8;
  font-style: italic;
  margin: 2px 0;
}
/* 人物对话：说话人粉色，正文近白 */
.sc-dialogue {
  font-size: 26px;
  line-height: 1.8;
  letter-spacing: 0.5px;
  color: #f3ecfa;
  margin: 2px 0;
}
.sc-speaker {
  color: #ff9ecb;
  font-weight: 600;
  text-shadow: 0 0 10px rgba(255, 158, 203, 0.35);
}
/* 旁白：浅白 */
.sc-narrator {
  font-size: 26px;
  line-height: 1.8;
  letter-spacing: 0.5px;
  color: #e6e0f0;
  margin: 2px 0;
}
/* 表格：表头淡紫底粉字，数据行浅色 */
.sc-table {
  display: flex;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  font-size: 15px;
  line-height: 1.6;
  letter-spacing: 0.3px;
  border-radius: 6px;
  margin: 2px 0;
  overflow: hidden;
}
.sc-table-head {
  background: rgba(201, 168, 255, 0.22);
  color: #ffc4e2;
  font-weight: 700;
  border: 1px solid rgba(201, 168, 255, 0.35);
}
.sc-table-row {
  background: rgba(201, 168, 255, 0.08);
  color: #e6e0f0;
  border: 1px solid rgba(201, 168, 255, 0.12);
}
.sc-table-row:hover {
  background: rgba(255, 158, 203, 0.12);
}
.sc-tcell {
  flex: 1;
  min-width: 0;
  padding: 6px 8px;
  word-break: break-word;
  overflow-wrap: anywhere;
}
/* 关于：收窄宽度 + 透明模糊背景（高特异性覆盖 .vn-modal-card 的宽度约束） */
.vn-modal-card.about-card {
  max-width: 60%;
  min-width: 320px;
  padding: 10px 10px 10px;
  background: rgba(18, 12, 30, 0.7);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border: 1px solid rgba(255, 160, 210, 0.35);
  box-shadow: 0 12px 44px rgba(0, 0, 0, 0.4);
}
/* 标题：粉色 */
.vn-modal-card.about-card h3 {
  color: #ff9ecb;
  font-size: 26px;
}
/* 标题下方粉色渐变分隔线 */
.vn-modal-card.about-card h3::after {
  content: '';
  display: block;
  width: 56px;
  height: 2px;
  margin: 12px auto 4px;
  background: linear-gradient(90deg, transparent, #ff9ecb, transparent);
  border-radius: 2px;
}
/* 正文：24px，内容超高时内部滚动，保持弹窗比例 */
.vn-modal-card.about-card .about-text {
  max-width: 100%;
  font-size: 24px;
  line-height: 1.8;
  letter-spacing: 1px;
  color: rgba(255, 235, 248, 0.95);
  text-align: left;
  max-height: 62vh;
  overflow-y: auto;
  padding-right: 6px;
  scrollbar-gutter: stable;
}
.vn-modal-card.about-card .about-text p {
  margin: 12px 0;
}
/* 正文细滚动条 */
.vn-modal-card.about-card .about-text::-webkit-scrollbar {
  width: 4px;
}
.vn-modal-card.about-card .about-text::-webkit-scrollbar-thumb {
  background: rgba(255, 158, 203, 0.5);
  border-radius: 4px;
}
.vn-modal-card.about-card .about-text::-webkit-scrollbar-track {
  background: transparent;
}
/* 落款（最后一行）居中粉色小字 */
.vn-modal-card.about-card .about-text p:last-child {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 160, 210, 0.3);
  text-align: center;
  color: #ff9ecb;
  font-size: 16px;
  letter-spacing: 3px;
}

/* ================= 游戏画面 ================= */
.vn-game {
  position: absolute;
  inset: 0;
  background: #0b0d17;
  overflow: hidden;
  transition: opacity 0.9s ease;
}
/* 场景过渡：整个游戏画面淡出（配合过渡层使用） */
.vn-game.game-faded {
  opacity: 0;
  pointer-events: none;
}

/* ================= 场景过渡层 ================= */
.trans-layer {
  position: absolute;
  inset: 0;
  z-index: 12;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0b0d17;
}
.trans-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.fade-media-enter-active,
.fade-media-leave-active {
  transition: opacity 0.9s ease;
}
.fade-media-enter,
.fade-media-leave-to {
  opacity: 0;
}

/* ================= 画面切换淡入淡出（结局停留后缓慢淡出 → 标题页淡入） ================= */
.screen-fade-enter-active,
.screen-fade-leave-active {
  transition: opacity 1s ease;
}
.screen-fade-enter,
.screen-fade-leave-to {
  opacity: 0;
}
.layer-bg, .layer-cg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
}
video.layer-bg {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.layer-cg {
  z-index: 3;
}
.layer-stand {
  position: absolute;
  inset: 0;
  z-index: 4;
  pointer-events: none;
}
.stand-item {
  position: absolute;
  bottom: 0;
  height: 68%;
  pointer-events: none;
}
.stand-item img {
  height: 100%;
  filter: drop-shadow(0 0 24px rgba(0,0,0,0.5));
}
.stand-left {
  left: 4%;
}
.stand-center {
  left: 50%;
  transform: translateX(-50%);
}
.stand-right {
  right: 4%;
}
.fade-stand-enter-active, .fade-stand-leave-active {
  transition: opacity 0.6s ease, transform 0.6s ease;
}
.fade-stand-enter, .fade-stand-leave-to {
  opacity: 0;
  transform: translateY(24px);
}
.fade-bg-enter-active, .fade-bg-leave-active {
  transition: opacity 0.9s ease;
}
.fade-bg-enter, .fade-bg-leave-to {
  opacity: 0;
}
.fx-layer {
  position: absolute;
  inset: 0;
  z-index: 4;
  pointer-events: none;
}
.fx-layer.shake {
  animation: vnShake 0.6s ease;
}
@keyframes vnShake {
  0%, 100% { transform: translate(0, 0); }
  15% { transform: translate(-10px, 6px); }
  30% { transform: translate(9px, -5px); }
  45% { transform: translate(-7px, 4px); }
  60% { transform: translate(6px, -3px); }
  75% { transform: translate(-4px, 2px); }
}
.fx-flash {
  position: absolute;
  inset: 0;
  opacity: 0.85;
  animation: flashOut 0.5s ease forwards;
}
@keyframes flashOut {
  from { opacity: 0.85; }
  to { opacity: 0; }
}

/* ================= 章节标题 ================= */
.chapter-layer {
  position: absolute;
  inset: 0;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(6, 5, 14, 0.72);
}
.chapter-card {
  text-align: center;
  color: #ffe6f4;
}
.chapter-label {
  font-size: 13px;
  letter-spacing: 8px;
  color: #ff9ecb;
  margin-bottom: 16px;
}
.chapter-card h2 {
  font-size: 46px;
  letter-spacing: 10px;
  margin: 0 0 12px;
}
.chapter-card .chapter-sub {
  font-size: 18px;
  letter-spacing: 5px;
  color: #ffd7ec;
}
.chapter-tip {
  margin-top: 30px;
  font-size: 12px;
  letter-spacing: 3px;
  color: rgba(255, 235, 248, 0.5);
  animation: blink 1.6s infinite;
}
@keyframes blink {
  0%, 100% { opacity: 0.25; }
  50% { opacity: 1; }
}
.fade-chapter-enter-active, .fade-chapter-leave-active {
  transition: opacity 0.8s ease;
}
.fade-chapter-enter, .fade-chapter-leave-to {
  opacity: 0;
}

/* ================= 选项 ================= */
.option-layer {
  position: absolute;
  inset: 0;
  z-index: 7;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(8, 6, 18, 0.55);
}
.option-box {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
}
.option-hint {
  color: #ffb8dd;
  font-size: 15px;
  letter-spacing: 5px;
  margin-bottom: 8px;
}
.vn-btn.option {
  min-width: 320px;
  font-size: 16px;
  font-family: 'KaiTi', 'STKaiti', '楷体', serif;
}

/* ================= 对话框 ================= */
.dialog-wrap {
  position: absolute;
  left: 20%;
  right: 20%;
  bottom: 4.5%;
  z-index: 5;
  cursor: pointer;
}
.dialog-box {
  position: relative;
  //background: linear-gradient(180deg, rgba(22, 18, 36, 0.92), rgba(14, 10, 26, 0.94));
  border: 1px solid rgba(255, 160, 210, 0.35);
  border-radius: 14px;
  padding: 15px 15px 15px;
  min-height: 118px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
}
.speaker-tag {
  position: absolute;
  top: -20px;
  left: 22px;
  padding: 6px 20px;
  border-radius: 10px;
  color: #fff;
  font-size: 15px;
  letter-spacing: 2px;
  font-family: 'KaiTi', 'STKaiti', '楷体', serif;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
}
/* 姓名框位置：left(默认) / center / right */
.speaker-tag.sp-center {
  left: 50%;
  transform: translateX(-50%);
}
.speaker-tag.sp-right {
  left: auto;
  right: 22px;
}
.dialog-text {
  color: #3a8ee6;
  line-height: 1.85;
  font-size: 22px !important;
  letter-spacing: 1px;
  word-break: break-all;
  padding-top: 6px;
  font-family: 'KaiTi', 'STKaiti', '楷体', serif;
}
.cursor {
  color: #ff9ecb;
  animation: blink 0.8s infinite;
  margin-left: 2px;
}
.next-arrow {
  position: absolute;
  right: 22px;
  bottom: 14px;
  color: #ff9ecb;
  font-size: 14px;
  animation: bob 1s ease-in-out infinite;
}
@keyframes bob {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(5px); }
}

/* ================= 系统栏 ================= */
.sys-bar {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 8;
  display: flex;
  gap: 8px;
  transition: top 0.55s cubic-bezier(0.4, 0, 0.2, 1);
}
/* 导航栏展开时，系统栏下移到导航栏下方避让（导航栏高度 60px + 14px 间距） */
.sys-bar.sys-under-nav {
  top: 74px;
}
.sys-btn {
  padding: 6px 14px;
  border: 1px solid rgba(255, 190, 222, 0.4);
  background: rgba(20, 14, 32, 0.6);
  color: #ffd7ec;
  font-size: 12px;
  border-radius: 16px;
  cursor: pointer;
  letter-spacing: 2px;
  transition: all 0.2s;
  backdrop-filter: blur(4px);
}
.sys-btn:hover, .sys-btn.active {
  background: rgba(255, 140, 195, 0.35);
  border-color: #ff9ecb;
}

/* ================= 弹窗面板 ================= */
.vn-modal {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  //background: rgba(5, 4, 12, 0.72);
  backdrop-filter: blur(3px);
}
.vn-modal-card {
  position: relative;
  background: linear-gradient(180deg, rgba(38, 28, 52, 0.97), rgba(20, 14, 32, 0.98));
  border: 1px solid rgba(255, 160, 210, 0.4);
  border-radius: 16px;
  padding: 5px 5px;
  min-width: 380px;
  max-width: 100%;
  color: #ffe6f4;
  text-align: center;
  box-shadow: 0 16px 60px rgba(0, 0, 0, 0.6);
}
.vn-modal-card h3 {
  margin: 0 0 1px;
  font-size: 20px;
  letter-spacing: 5px;
  color: #ffb8dd;
}
/* 弹窗右上角 × 关闭按钮 */
.vn-modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid rgba(255, 160, 210, 0.45);
  background: rgba(34, 22, 50, 0.55);
  color: #ffd7ec;
  font-size: 22px;
  line-height: 1;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}
.vn-modal-close:hover {
  background: rgba(255, 140, 195, 0.4);
  border-color: #ff9ecb;
  color: #fff;
}
.setting-row {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 12px 0;
  font-size: 14px;
  letter-spacing: 1px;
}
.setting-row input[type=range] {
  flex: 1;
  accent-color: #ff7fb6;
}
.setting-row .val {
  min-width: 34px;
  color: #ff9ecb;
}
/* 设置面板内静音按钮 */
.setting-row .setting-mute {
  min-width: 150px;
  margin-top: 0;
  padding: 8px 16px;
  font-size: 13px;
}
.save-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 10px;
}
.save-slot {
  border: 1px solid rgba(255, 190, 222, 0.3);
  border-radius: 10px;
  padding: 12px 8px;
  cursor: pointer;
  background: rgba(10, 8, 20, 0.5);
  transition: all 0.2s;
}
.save-slot:hover {
  background: rgba(255, 140, 195, 0.2);
  border-color: #ff9ecb;
}
.slot-title {
  font-size: 13px;
  letter-spacing: 2px;
  margin-bottom: 6px;
  color: #ffd7ec;
}
.slot-info {
  font-size: 11px;
  color: rgba(255, 235, 248, 0.6);
  line-height: 1.4;
}

/* ================= 结局 ================= */
.ending-screen .title-main {
  font-size: 54px;
}
.ending-label {
  font-size: 16px;
  letter-spacing: 10px;
  color: #ff9ecb;
  margin-bottom: 14px;
}
.ending-text {
  max-width: 620px;
  margin: 24px auto 34px;
  font-size: 15px;
  line-height: 2;
  letter-spacing: 1px;
  color: #ffe6f4;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.6);
}
</style>
